import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { clsx } from 'clsx';
import { useAuth } from '@/context';
import styles from './Sidebar.module.css';

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  permission?: string;
  tourTarget?: string;
}

interface NavCategory {
  id: string;
  label: string;
  icon: React.ReactNode;
  items: NavItem[];
}

interface SidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
}

// Category Icons
const CategoryIcons = {
  operations: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <rect x="2" y="2" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.5"/>
      <rect x="9" y="2" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.5"/>
      <rect x="2" y="9" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.5"/>
      <rect x="9" y="9" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.5"/>
    </svg>
  ),
  records: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M2 3C2 2.44772 2.44772 2 3 2H13C13.5523 2 14 2.44772 14 3V13C14 13.5523 13.5523 14 13 14H3C2.44772 14 2 13.5523 2 13V3Z" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M5 6H11M5 8.5H11M5 11H9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  ),
  compliance: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M8 1.5L2 4V7.5C2 11 4.5 13.5 8 14.5C11.5 13.5 14 11 14 7.5V4L8 1.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M5.5 8L7 9.5L10.5 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  reporting: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M2 14V5L5 3V14H2Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
      <path d="M6 14V7L9 5V14H6Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
      <path d="M10 14V3L13 1V14H10Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
    </svg>
  ),
  admin: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M8 2V4M8 12V14M2 8H4M12 8H14M3.76 3.76L5.17 5.17M10.83 10.83L12.24 12.24M3.76 12.24L5.17 10.83M10.83 5.17L12.24 3.76" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  ),
};

// Navigation Item Icons
const NavIcons = {
  dashboard: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M2.25 9L9 2.25L15.75 9M4.5 7.5V14.25C4.5 14.6642 4.83579 15 5.25 15H7.5V11.25C7.5 10.8358 7.83579 10.5 8.25 10.5H9.75C10.1642 10.5 10.5 10.8358 10.5 11.25V15H12.75C13.1642 15 13.5 14.6642 13.5 14.25V7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  digitization: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <rect x="2.25" y="2.25" width="5.25" height="5.25" rx="0.75" stroke="currentColor" strokeWidth="1.5"/>
      <rect x="10.5" y="2.25" width="5.25" height="5.25" rx="0.75" stroke="currentColor" strokeWidth="1.5"/>
      <rect x="2.25" y="10.5" width="5.25" height="5.25" rx="0.75" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M13.125 10.5V15.75M10.5 13.125H15.75" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  ),
  fileFinder: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <circle cx="7.5" cy="7.5" r="4.5" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M11 11L15.75 15.75" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      <path d="M7.5 5.25V9.75M5.25 7.5H9.75" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  ),
  staffPortal: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <circle cx="9" cy="5.25" r="2.75" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M3 15.75V14.25C3 12.0409 4.79086 10.25 7 10.25H11C13.2091 10.25 15 12.0409 15 14.25V15.75" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  ),
  records: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M3.75 3.75C3.75 2.92157 4.42157 2.25 5.25 2.25H10.5L14.25 6V14.25C14.25 15.0784 13.5784 15.75 12.75 15.75H5.25C4.42157 15.75 3.75 15.0784 3.75 14.25V3.75Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M10.5 2.25V6H14.25" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  pfiles: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M15.75 14.25C15.75 15.0784 15.0784 15.75 14.25 15.75H3.75C2.92157 15.75 2.25 15.0784 2.25 14.25V3.75C2.25 2.92157 2.92157 2.25 3.75 2.25H7.5L9 4.5H14.25C15.0784 4.5 15.75 5.17157 15.75 6V14.25Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M6 9H12M6 12H10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  ),
  tasks: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M8.25 4.5H14.25M8.25 9H14.25M8.25 13.5H14.25M3.75 4.5H3.7575M3.75 9H3.7575M3.75 13.5H3.7575" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  search: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <circle cx="8.25" cy="8.25" r="5.25" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M12.375 12.375L15.75 15.75" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  ),
  security: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M9 1.5L2.25 4.5V8.625C2.25 12.5625 5.0625 16.1875 9 17.25C12.9375 16.1875 15.75 12.5625 15.75 8.625V4.5L9 1.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M6 9L8 11L12 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  compliance: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <rect x="3" y="2.25" width="12" height="13.5" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M6 6.75H12M6 9.75H12M6 12.75H9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      <path d="M3 6H15" stroke="currentColor" strokeWidth="1.5"/>
    </svg>
  ),
  archival: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M2.25 3H15.75V5.25H2.25V3Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
      <path d="M3.75 5.25V14.25C3.75 14.6642 4.08579 15 4.5 15H13.5C13.9142 15 14.25 14.6642 14.25 14.25V5.25" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M7.5 9H10.5M7.5 11.5H10.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  ),
  retirement: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <circle cx="9" cy="5.25" r="2.75" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M3 15.75V14.25C3 12.0409 4.79086 10.25 7 10.25H11C13.2091 10.25 15 12.0409 15 14.25V15.75" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      <path d="M12.75 12.75L15 15M12.75 15L15 12.75" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  ),
  reports: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M2.25 15V7L5.25 4.5V15H2.25Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
      <path d="M6.75 15V9L9.75 6.5V15H6.75Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
      <path d="M11.25 15V5L14.25 2.5V15H11.25Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
    </svg>
  ),
  notifications: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M13.5 6C13.5 4.80653 13.0259 3.66193 12.182 2.81802C11.3381 1.97411 10.1935 1.5 9 1.5C7.80653 1.5 6.66193 1.97411 5.81802 2.81802C4.97411 3.66193 4.5 4.80653 4.5 6C4.5 11.25 2.25 12.75 2.25 12.75H15.75C15.75 12.75 13.5 11.25 13.5 6Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M10.2975 15.75C10.1657 15.9773 9.9764 16.166 9.74868 16.2971C9.52097 16.4283 9.26278 16.4973 9 16.4973C8.73722 16.4973 8.47903 16.4283 8.25132 16.2971C8.0236 16.166 7.8343 15.9773 7.7025 15.75" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  recycleBin: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M3.75 5.25H14.25M7.5 5.25V3.75C7.5 3.33579 7.83579 3 8.25 3H9.75C10.1642 3 10.5 3.33579 10.5 3.75V5.25M13.5 5.25V14.25C13.5 14.6642 13.1642 15 12.75 15H5.25C4.83579 15 4.5 14.6642 4.5 14.25V5.25H13.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  users: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M12.75 14.25C12.75 12.5931 10.3995 11.25 7.5 11.25C4.60051 11.25 2.25 12.5931 2.25 14.25M7.5 9C5.63604 9 4.125 7.48896 4.125 5.625C4.125 3.76104 5.63604 2.25 7.5 2.25C9.36396 2.25 10.875 3.76104 10.875 5.625C10.875 7.48896 9.36396 9 7.5 9ZM15.75 14.25C15.75 12.9643 14.3033 11.8393 12.375 11.4103M12 9C13.2426 9 14.25 7.99264 14.25 6.75C14.25 5.50736 13.2426 4.5 12 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  departments: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M6.75 15V6.75M6.75 6.75V3H11.25V6.75M6.75 6.75H3V15H15V6.75H11.25M11.25 6.75V15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  roles: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path d="M9 10.5C10.6569 10.5 12 9.15685 12 7.5C12 5.84315 10.6569 4.5 9 4.5C7.34315 4.5 6 5.84315 6 7.5C6 9.15685 7.34315 10.5 9 10.5Z" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M9 10.5C5.96243 10.5 3.5 12.2147 3.5 14.25H14.5C14.5 12.2147 12.0376 10.5 9 10.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M12.75 3L14.25 4.5L12.75 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  workflows: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <rect x="2.25" y="2.25" width="4.5" height="4.5" rx="0.75" stroke="currentColor" strokeWidth="1.5"/>
      <rect x="11.25" y="2.25" width="4.5" height="4.5" rx="0.75" stroke="currentColor" strokeWidth="1.5"/>
      <rect x="11.25" y="11.25" width="4.5" height="4.5" rx="0.75" stroke="currentColor" strokeWidth="1.5"/>
      <rect x="2.25" y="11.25" width="4.5" height="4.5" rx="0.75" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M6.75 4.5H11.25M13.5 6.75V11.25M6.75 13.5H11.25" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  ),
  settings: (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <circle cx="9" cy="9" r="2.25" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M14.55 11.25C14.4093 11.5684 14.3759 11.9233 14.4545 12.2615C14.5332 12.5996 14.7198 12.9037 14.9867 13.1287L15.0345 13.1765C15.2518 13.3937 15.4243 13.6516 15.5423 13.9355C15.6602 14.2194 15.7212 14.5237 15.7219 14.8311C15.7226 15.1384 15.6629 15.443 15.5462 15.7274C15.4295 16.0119 15.2581 16.2705 15.0418 16.4887C14.8254 16.707 14.5683 16.8807 14.2848 17C14.0014 17.1193 13.6973 17.1818 13.39 17.1839C13.0826 17.1861 12.7777 17.1278 12.4925 17.0124C12.2074 16.8971 11.9479 16.727 11.7285 16.512L11.6807 16.4642C11.4557 16.1973 11.1516 16.0107 10.8135 15.9321C10.4753 15.8534 10.1204 15.8868 9.80205 16.0275V16.125C9.80205 16.7463 9.55515 17.3421 9.11569 17.7816C8.67623 18.221 8.08043 18.468 7.45918 18.468C6.83792 18.468 6.24212 18.221 5.80266 17.7816C5.3632 17.3421 5.1163 16.7463 5.1163 16.125V16.0777C5.02991 15.7486 4.84219 15.4553 4.57945 15.2408C4.31671 15.0264 3.99298 14.9023 3.65505 14.886C3.33669 14.7453 2.98178 14.7119 2.64363 14.7906C2.30549 14.8692 2.00136 15.0558 1.7763 15.3227L1.7285 15.3705C1.51127 15.5879 1.25339 15.7603 0.969455 15.8783C0.685518 15.9962 0.381241 16.0572 0.0738893 16.058C-0.233463 16.0587 -0.538073 15.999 -0.822497 15.8823C-1.10692 15.7656 -1.36555 15.5942 -1.5838 15.3778C-1.80205 15.1615 -1.97567 14.9043 -2.095 14.6209C-2.21433 14.3374 -2.27682 14.0334 -2.27897 13.726C-2.28112 13.4186 -2.2229 13.1137 -2.10757 12.8286C-1.99225 12.5434 -1.82213 12.2839 -1.607 12.0645L-1.55925 12.0167C-1.2924 11.7917 -1.10575 11.4876 -1.02712 11.1494C-0.948496 10.8113 -0.981858 10.4564 -1.1225 10.138" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
};

const navCategories: NavCategory[] = [
  {
    id: 'operations',
    label: 'Operations',
    icon: CategoryIcons.operations,
    items: [
      { label: 'Dashboard', path: '/dashboard', icon: NavIcons.dashboard, tourTarget: 'sidebar-dashboard' },
      { label: 'Digitization', path: '/digitization', icon: NavIcons.digitization, permission: 'digitization:manage' },
      { label: 'File Finder', path: '/smart-registry', icon: NavIcons.fileFinder, permission: 'registry:view' },
      { label: 'Staff Portal', path: '/staff-portal', icon: NavIcons.staffPortal },
    ],
  },
  {
    id: 'records',
    label: 'Records Management',
    icon: CategoryIcons.records,
    items: [
      { label: 'Staff Records', path: '/records', icon: NavIcons.records, tourTarget: 'sidebar-records' },
      { label: 'File Jackets', path: '/pfiles', icon: NavIcons.pfiles, tourTarget: 'sidebar-pfiles' },
      { label: 'My Tasks', path: '/tasks', icon: NavIcons.tasks, tourTarget: 'sidebar-tasks' },
      { label: 'Search', path: '/search', icon: NavIcons.search, tourTarget: 'sidebar-search' },
    ],
  },
  {
    id: 'compliance',
    label: 'Compliance & Archival',
    icon: CategoryIcons.compliance,
    items: [
      { label: 'Security & Audit', path: '/security-audit', icon: NavIcons.security, permission: 'security:view' },
      { label: 'Retention & Custody', path: '/compliance', icon: NavIcons.compliance, permission: 'compliance:view' },
      { label: 'Records Archival', path: '/records-archival', icon: NavIcons.archival, permission: 'archive_transfers:read' },
      { label: 'Retirement Processing', path: '/retirement-processing', icon: NavIcons.retirement, permission: 'retirement:read' },
    ],
  },
  {
    id: 'reporting',
    label: 'Reporting',
    icon: CategoryIcons.reporting,
    items: [
      { label: 'Reports', path: '/reports', icon: NavIcons.reports },
      { label: 'Notifications', path: '/notifications', icon: NavIcons.notifications },
      { label: 'Recycle Bin', path: '/recycle-bin', icon: NavIcons.recycleBin },
    ],
  },
  {
    id: 'admin',
    label: 'Administration',
    icon: CategoryIcons.admin,
    items: [
      { label: 'Users', path: '/admin/users', icon: NavIcons.users, permission: 'users:read', tourTarget: 'sidebar-admin-users' },
      { label: 'Directorate/Unit', path: '/admin/departments', icon: NavIcons.departments, permission: 'departments:read' },
      { label: 'Roles', path: '/admin/roles', icon: NavIcons.roles, permission: 'roles:read' },
      { label: 'Workflows', path: '/admin/workflows', icon: NavIcons.workflows, permission: 'workflows:create_template', tourTarget: 'sidebar-admin-workflows' },
      { label: 'Settings', path: '/admin/settings', icon: NavIcons.settings, permission: 'settings:read' },
    ],
  },
];

export function Sidebar({ isOpen = true, onClose }: SidebarProps) {
  const { hasPermission } = useAuth();
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(['operations', 'records']) // Default expanded
  );

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  // Filter categories and their items based on permissions
  const filteredCategories = navCategories
    .map(category => ({
      ...category,
      items: category.items.filter(item => !item.permission || hasPermission(item.permission)),
    }))
    .filter(category => category.items.length > 0);

  // Close sidebar on navigation (mobile only)
  const handleNavClick = () => {
    if (onClose && window.innerWidth <= 768) {
      onClose();
    }
  };

  return (
    <>
      {/* Overlay for mobile */}
      {isOpen && onClose && (
        <div className={styles.overlay} onClick={onClose} />
      )}

      <aside className={clsx(styles.sidebar, isOpen && styles.open)} data-tour-target="sidebar">
        <nav className={styles.nav}>
          {filteredCategories.map((category, categoryIndex) => {
            const isExpanded = expandedCategories.has(category.id);

            return (
              <div
                key={category.id}
                className={styles.category}
                style={{ '--category-index': categoryIndex } as React.CSSProperties}
              >
                <button
                  className={clsx(styles.categoryHeader, isExpanded && styles.expanded)}
                  onClick={() => toggleCategory(category.id)}
                  aria-expanded={isExpanded}
                >
                  <span className={styles.categoryIcon}>{category.icon}</span>
                  <span className={styles.categoryLabel}>{category.label}</span>
                  <span className={styles.categoryChevron}>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </span>
                </button>

                <div className={clsx(styles.categoryItems, isExpanded && styles.expanded)}>
                  <ul className={styles.navList}>
                    {category.items.map((item, itemIndex) => (
                      <li
                        key={item.path}
                        style={{ '--item-index': itemIndex } as React.CSSProperties}
                      >
                        <NavLink
                          to={item.path}
                          onClick={handleNavClick}
                          className={({ isActive }) =>
                            clsx(styles.navItem, isActive && styles.active)
                          }
                          {...(item.tourTarget ? { 'data-tour-target': item.tourTarget } : {})}
                        >
                          <span className={styles.navIcon}>{item.icon}</span>
                          <span className={styles.navLabel}>{item.label}</span>
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </nav>

        {/* Decorative footer */}
        <div className={styles.sidebarFooter}>
          <div className={styles.footerAccent} />
        </div>
      </aside>
    </>
  );
}
